// PaymentStatusPage.js
import React, { useEffect, useState, useRef, useCallback } from 'react';
import { useSearchParams, useNavigate } from 'react-router-dom';
import './PaymentPage.css';
import { WA_CS_LINK } from './config/whatsapp';
import { useLanguage } from './i18n/LanguageContext';

const SUCCESS_STATUSES = ['ESIM_PUBLISHED', 'TOP_UP_COMPLETED'];
const STEP2_STATUSES = ['PAID'];
const STEP3_STATUSES = ['ESIM_ORDERED', 'ORDER_FULFILLED', 'ORDER_COMBO_ADDED', 'TOP_UP_ORDERED'];
const isFailedStatus = (s) => /FAIL|EXPIRE|CANCEL|REJECT|DECLINE/i.test(s || '');

// Which step (1-based) is currently in progress for a given status.
// CREATED → 1 (verifying payment), PAID → 2 (creating eSIM),
// ESIM_ORDERED/ORDER_FULFILLED/ORDER_COMBO_ADDED → 3 (sending to email),
// ESIM_PUBLISHED/TOP_UP_COMPLETED → terminal (all steps complete).
const statusToStep = (status) => {
    if (SUCCESS_STATUSES.includes(status)) return 3;
    if (STEP3_STATUSES.includes(status)) return 3;
    if (STEP2_STATUSES.includes(status)) return 2;
    return 1; // CREATED / unknown
};

// Step and heading copy live in paymentStatus.steps.<type> and
// paymentStatus.headings.<phase>.<type>.

const CheckIcon = () => (
    <svg width="14" height="14" viewBox="0 0 24 24" fill="none" aria-hidden="true">
        <polyline points="5,12 10,17 19,7" stroke="#fff" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round" />
    </svg>
);
const MailIcon = ({ color }) => (
    <svg width="14" height="14" viewBox="0 0 24 24" fill="none" aria-hidden="true">
        <rect x="3" y="5" width="18" height="14" rx="2" stroke={color} strokeWidth="2" />
        <path d="M4 7l8 6 8-6" stroke={color} strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" />
    </svg>
);
const CrossIcon = () => (
    <svg width="14" height="14" viewBox="0 0 24 24" fill="none" aria-hidden="true">
        <path d="M7 7l10 10M17 7L7 17" stroke="#fff" strokeWidth="2.5" strokeLinecap="round" />
    </svg>
);

const PaymentStatusPage = () => {
    const [searchParams] = useSearchParams();
    const orderId = searchParams.get('orderId') || searchParams.get('order_id');
    const [status, setStatus] = useState('');
    const [orderType, setOrderType] = useState('esim');
    const [loading, setLoading] = useState(true);
    const navigate = useNavigate();
    const { t } = useLanguage();
    const pollRef = useRef(null);

    const phase = isFailedStatus(status)
        ? 'failed'
        : SUCCESS_STATUSES.includes(status)
            ? 'success'
            : 'processing';
    const type = orderType === 'topup' ? 'topup' : 'esim';
    const steps = t(`paymentStatus.steps.${type}`);
    const copy = t(`paymentStatus.headings.${phase}.${type}`);

    const fetchStatus = useCallback(() => {
        if (!orderId) return;
        fetch(`https://api.artamobile.id/v1/order/${orderId}/status`)
            .then(res => res.json())
            .then(({ status, type }) => {
                setStatus(status);
                if (type) setOrderType(type);
                setLoading(false);
            })
            .catch(() => setLoading(false));
    }, [orderId]);

    // Poll until we reach a terminal state (success or failed).
    useEffect(() => {
        if (!orderId) { setLoading(false); return; }
        fetchStatus();
        pollRef.current = setInterval(fetchStatus, 5000);
        return () => clearInterval(pollRef.current);
    }, [orderId, fetchStatus]);

    useEffect(() => {
        if (phase !== 'processing' && pollRef.current) clearInterval(pollRef.current);
    }, [phase]);

    // On success, offer a gentle auto-redirect to the eSIM list (CTA is the primary path).
    useEffect(() => {
        if (phase !== 'success') return;
        const timer = setTimeout(() => navigate('/esims', { replace: true }), 6000);
        return () => clearTimeout(timer);
    }, [phase, navigate]);

    // Resolve each step's visual state for the current phase.
    const currentStep = statusToStep(status);
    const stepState = (idx) => {
        const step = idx + 1;
        if (phase === 'success') return 'completed';
        if (phase === 'failed') return step === 1 ? 'failed' : 'pending';
        if (step < currentStep) return 'completed';
        if (step === currentStep) return 'active';
        return 'pending';
    };

    const renderMarker = (idx, state) => {
        const isLast = idx === steps.length - 1;
        if (state === 'completed') return <span className="pp-step-marker completed"><CheckIcon /></span>;
        if (state === 'failed') return <span className="pp-step-marker failed"><CrossIcon /></span>;
        if (state === 'active') return <span className="pp-step-marker active"><span className="pp-step-dot" /></span>;
        // pending
        return (
            <span className="pp-step-marker pending">
                {isLast ? <MailIcon color="#BFBFBF" /> : <span className="pp-step-num">{idx + 1}</span>}
            </span>
        );
    };

    if (loading) {
        return <div className="loader-wrapper"><div className="loader" /></div>;
    }

    if (!orderId) {
        return (
            <div className="pp-page">
                <div className="pp-appbar">
                    <button className="pp-back-btn" onClick={() => navigate('/')} aria-label={t('common.back')}>
                        <img src={`${process.env.PUBLIC_URL}/icons/arrow-left.svg`} width={16} height={16} alt="" />
                    </button>
                    <h1 className="pp-appbar-title">{t('paymentStatus.orderStatus')}</h1>
                </div>
                <div className="pp-scroll"><div className="pp-content">
                    <p className="pp-status-sub" style={{ textAlign: 'center', marginTop: 40 }}>
                        {t('paymentStatus.orderNotFound')}
                    </p>
                </div></div>
            </div>
        );
    }

    return (
        <div className="pp-page">
            {/* ── TopAppBar ── */}
            <div className="pp-appbar">
                <button className="pp-back-btn" onClick={() => navigate('/')} aria-label={t('common.back')}>
                    <img src={`${process.env.PUBLIC_URL}/icons/arrow-left.svg`} width={16} height={16} alt="" />
                </button>
                <h1 className="pp-appbar-title">{type === 'topup' ? t('paymentStatus.appbarTopup') : t('paymentStatus.appbarEsim')}</h1>
            </div>

            <div className="pp-scroll">
                <div className="pp-content">
                    {/* ── Status header ── */}
                    <div className="pp-status-head">
                        <span className={`pp-status-badge ${phase}`}>
                            <span className="pp-status-dot" />
                            {copy.pill}
                        </span>
                        <h2 className="pp-status-title">{copy.title}</h2>
                        <p className="pp-status-sub">{copy.sub}</p>
                    </div>

                    {/* ── Stepper ── */}
                    <div className="pp-stepper">
                        {steps.map((s, idx) => {
                            const state = stepState(idx);
                            const isLast = idx === steps.length - 1;
                            return (
                                <div key={idx} className={`pp-step ${state}`}>
                                    <div className="pp-step-rail">
                                        {renderMarker(idx, state)}
                                        {!isLast && (
                                            <span className={`pp-step-line${state === 'completed' ? ' completed' : ''}`} />
                                        )}
                                    </div>
                                    <div className="pp-step-body">
                                        <p className="pp-step-title">{s.title}</p>
                                        <p className="pp-step-sub">{s.sub}</p>
                                    </div>
                                </div>
                            );
                        })}
                    </div>

                    {/* ── Ada Kendala? ── */}
                    <div className="pp-help-card">
                        <div className="pp-help-content">
                            <div className="pp-help-texts">
                                <p className="pp-help-title">{t('help.title')}</p>
                                <p className="pp-help-desc">{t('help.desc')}</p>
                            </div>
                            <a
                                className="pp-help-chat"
                                href={WA_CS_LINK}
                                target="_blank"
                                rel="noopener noreferrer"
                            >
                                {t('help.cta')}
                            </a>
                        </div>
                        <img
                            className="pp-help-illustration"
                            src={`${process.env.PUBLIC_URL}/icons/ada-kendala-illustration.png`}
                            alt=""
                            aria-hidden="true"
                        />
                    </div>

                    {phase === 'failed' && (
                        <button className="pp-cta-secondary" onClick={() => navigate('/', { replace: true })}>
                            {t('paymentStatus.backHome')}
                        </button>
                    )}
                </div>
            </div>

            {/* ── Bottom CTA (success / failed) ── */}
            {phase === 'success' && (
                <div className="pp-bottom-bar pp-bottom-bar--single">
                    <button className="pp-pay-btn" onClick={() => navigate('/esims', { replace: true })}>
                        {t('paymentStatus.viewEsims')}
                    </button>
                </div>
            )}
            {phase === 'failed' && (
                <div className="pp-bottom-bar pp-bottom-bar--single">
                    <button className="pp-pay-btn" onClick={() => navigate(-1)}>
                        {t('paymentStatus.retry')}
                    </button>
                </div>
            )}
        </div>
    );
};

export default PaymentStatusPage;
